// shadcn/ui standard utility — Tailwind class merge
import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * A tailwind-merge a PROJEKT SAJÁT fontSize-skáláját nem ismeri (`read-sm`…`read-3xl`, `2xs`, `md`
 * — lásd tailwind.config.cjs). Bővítés nélkül ezeket a heurisztika SZÍN-osztálynak nézi, és a
 * mellettük álló valódi szín kiüti a MÉRETET: `cn('text-read-sm', 'text-subtle')` kimérten
 * `text-subtle`-t adott — a méret nyom nélkül eltűnt.
 *
 * Ez néma vesztés, és pontosan az a fajta, amit nehéz észrevenni: a felület megjelenik, csak
 * rossz méretben. A statikus `className="…"` literálok (a repó többsége) NEM érintettek — ott nincs
 * merge —, a `cn`-en át összerakott osztályok viszont igen. A javítás ezért a `cn`-ben, egyszer.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        { text: ['2xs', 'md', 'read-sm', 'read-base', 'read-lg', 'read-xl', 'read-2xl', 'read-3xl'] },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Jogosultsági hiba felismerése egy react-query `error`-ból. A ts-rest kliens a nem-2xx választ
 * `{ status, body }` objektumként dobja (a queryFn `throw`-ol) — így egy 401/403 ide kerül. Hálózati/
 * szerver-hiba (throw-olt Error vagy 5xx) NEM jogosultsági hiba → általános hiba-állapot jár.
 * A megjelenítés ez alapján dönt „nincs jogod" vs. „nem sikerült betölteni" között — belső jog-részlet nélkül.
 */
export function isForbiddenError(error: unknown): boolean {
  return statusOf(error) === 403;
}

/**
 * 401 — a munkamenet MÉG nem áll (hidegindításkor a néma token-megújítás fut), NEM jog-hiány.
 *
 * Miért külön a 403-tól: a kettő egybemosva a kezdőlap hidegindításkor egy pillanatra a „nincs
 * hozzáférésed" képernyőt villantotta fel egy teljesen jogosult felhasználónak — a megújítás
 * befejeztével aztán rendben betöltött. A 401 helyes válasza a VÁRAKOZÁS (a `requireAuth`
 * amúgy is a login felé visz, ha tényleg nincs munkamenet), a 403-é a magyarázó képernyő.
 */
export function isUnauthenticatedError(error: unknown): boolean {
  return statusOf(error) === 401;
}

function statusOf(error: unknown): number | null {
  if (typeof error !== 'object' || error === null || !('status' in error)) return null;
  const { status } = error;
  return typeof status === 'number' ? status : null;
}
